import React, { useEffect, useMemo, useRef, useState } from "react";
import "./LightboxSearchSelect.css";

const labelFor = (o) => `${o.name} (by ${o.created_user})`;

const LightboxSearchSelect = ({ id, name, value, options, placeholder, disabled, hasError, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => String(o.id) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.name, o.created_user, o.doc_title].some((v) => (v ?? "").toLowerCase().includes(q))
    );
  }, [options, query]);

  const [dropUp, setDropUp] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const trigger = wrapRef.current.getBoundingClientRect();
    let clip = wrapRef.current.parentElement;
    while (clip && !/(auto|scroll|hidden)/.test(getComputedStyle(clip).overflowY)) clip = clip.parentElement;
    const bounds = clip ? clip.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const below = bounds.bottom - trigger.bottom;
    setDropUp(below < 300 && trigger.top - bounds.top > below);
    setQuery("");
    const idx = options.findIndex((o) => String(o.id) === String(value));
    setActive(idx >= 0 ? idx : 0);
    searchRef.current?.focus();
    const close = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (o) => {
    setOpen(false);
    if (String(o.id) !== String(value)) onChange({ target: { name, value: String(o.id) } });
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="lb-search-select mb-2" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`form-select lb-search-select__trigger${hasError ? " has-error" : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected ? `KDF: ${selected.doc_title}\nLightbox: ${labelFor(selected)}` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? labelFor(selected) : placeholder}
      </button>
      {open && (
        <div className={`lb-search-select__panel${dropUp ? " lb-search-select__panel--up" : ""}`}>
          <input
            ref={searchRef}
            type="text"
            className="lb-search-select__search"
            placeholder="Search by lightbox, creator or KDF"
            aria-label="Search lightboxes"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <ul className="lb-search-select__list" role="listbox" ref={listRef}>
            {filtered.length === 0 && <li className="lb-search-select__empty">No lightboxes match "{query}"</li>}
            {filtered.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={String(o.id) === String(value)}
                className={`lb-search-select__option${i === active ? " is-active" : ""}${String(o.id) === String(value) ? " is-selected" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o)}
              >
                <span className="lb-search-select__name">{labelFor(o)}</span>
                <span className="lb-search-select__kdf">{o.doc_title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LightboxSearchSelect;
